import { CheckboxCell, CheckboxHeader } from '@/components/table/DataTable';
import { Badge } from '@/components/ui/badge';
import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/drodown-menu-custom';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  useGetFullTextScreeningService,
  useUpdatePaperScreeningResultJustificationService,
  useUpdatePaperScreeningResultService,
  useUpdatePaperScreeningValidationStatusService,
  useUploadWorkPdfService,
} from '@/services/literatureReviews';
import { GetFullTextScreeningResultsResponse } from '@ditto/shared';
import { ColumnDef } from '@tanstack/react-table';
import {
  Ban,
  BookOpen,
  Check,
  ChevronDown,
  Circle,
  CircleHelp,
  ExternalLink,
  Pencil,
  Plus,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

export const useColumns = (
  id: string | undefined,
  setSelectedPaperScreening: (
    paperScreening:
      | GetFullTextScreeningResultsResponse['data'][number]
      | undefined
  ) => void,
  data: GetFullTextScreeningResultsResponse['data']
): ColumnDef<GetFullTextScreeningResultsResponse['data'][number]>[] => {
  const { fullTextScreening } = useGetFullTextScreeningService(id);
  const columns: ColumnDef<
    GetFullTextScreeningResultsResponse['data'][number]
  >[] = useMemo(() => {
    const allCriteria = fullTextScreening?.criteria ?? [];
    const criteriaColumns: ColumnDef<
      GetFullTextScreeningResultsResponse['data'][number]
    >[] = allCriteria.map(criteria => ({
      id: criteria.id,
      header: () => (
        <Text variant="body-sm" color="primary">
          {criteria.name}
        </Text>
      ),
      meta: {
        headerClassName:
          'w-[400px] min-w-[400px] max-w-[400px] border-r border-gray-200',
        cellClassName:
          'w-[400px] min-w-[400px] max-w-[400px] align-top border-r border-gray-200',
      },
      cell: ({ row }) => {
        const result = row.original.criteriaResults.find(
          cr => cr.criteriaId === criteria.id
        );
        const handleClick = () => {
          setSelectedPaperScreening(row.original);
        };

        const hasData = row.original.criteriaResults.some(
          criteriaResult =>
            criteriaResult.textValue !== undefined ||
            criteriaResult.generationStatus === 'completed' ||
            criteriaResult.generationStatus === 'error'
        );

        const isGenerating = row.original.isGenerating;

        if (isGenerating) {
          return (
            <FlexCol w="full" h="full" p="2">
              <Skeleton className="h-4" />
            </FlexCol>
          );
        }

        if (!hasData) {
          return null;
        }

        return (
          <Box
            onClick={handleClick}
            h="full"
            w="full"
            className="cursor-pointer"
          >
            <Text variant="body-sm" color="primary">
              {result?.textValue ?? 'Not found'}
            </Text>
          </Box>
        );
      },
    }));
    return [
      {
        id: 'select',
        header: ({ table }) => {
          return <CheckboxHeader table={table} />;
        },
        cell: ({ row }) => {
          return <CheckboxCell row={row} />;
        },
        meta: {
          headerClassName: 'w-[50px] min-w-[50px] max-w-[50px]',
          cellClassName: 'w-[50px] min-w-[50px] max-w-[50px]',
        },
      },
      {
        id: 'work',
        meta: {
          headerClassName:
            'w-[350px] min-w-[350px] max-w-[350px] border-r border-gray-200',
          cellClassName:
            'w-[350px] min-w-[350px] max-w-[350px] h-[185px] align-top border-r border-gray-200',
        },
        header: () => (
          <Text variant="body-sm" color="primary">
            Paper
          </Text>
        ),
        cell: ({ row }) => {
          const work = row.original.work;
          const fileInputRef = useRef<HTMLInputElement>(null);
          const { uploadWorkPdf, isUploading } = useUploadWorkPdfService();

          const handleClick = () => {
            setSelectedPaperScreening(row.original);
          };

          const handleUploadClick = () => {
            fileInputRef.current?.click();
          };

          const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            if (file.type !== 'application/pdf') {
              toast.error('Please upload a PDF file');
              return;
            }
            uploadWorkPdf(
              {
                file,
                paperScreeningId: row.original.id,
              },
              {
                onSuccess: () => {
                  toast.success('PDF uploaded successfully. Analysis started.');
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                },
                onError: () => {
                  toast.error('Upload failed');
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                },
              }
            );
          };

          let textToDisplay, icon;
          if (work.accessType === 'open_access') {
            textToDisplay = 'Full paper available';
            icon = <Circle className="w-2 h-2 fill-green-500 text-green-500" />;
          }
          if (work.accessType === 'restricted_access') {
            textToDisplay = 'Full paper not available';
            icon = <Circle className="w-2 h-2 fill-red-500 text-red-500" />;
          }

          const shouldDisplayAccessType =
            !work.accessType || work.accessType !== 'unknown';

          return (
            <FlexCol gap="1">
              <FlexCol
                className="w-full h-full p-2 gap-2 cursor-pointer"
                alignItems="start"
                justifyContent="start"
                onClick={handleClick}
              >
                <Text variant="body-sm" color="primary">
                  {work.name}
                </Text>
                <Ellipsis lineClamp={3} hasTooltip asChild>
                  <Text variant="body-xs" color="tertiary">
                    {work.authors}
                  </Text>
                </Ellipsis>
                <FlexRow
                  alignItems="center"
                  justifyContent="start"
                  className="gap-2"
                >
                  <BookOpen className="w-4 h-4 text-tertiary" />
                  <Ellipsis lineClamp={2} hasTooltip asChild>
                    <Text variant="body-xs" color="tertiary" w="fit">
                      {work.journal},{' '}
                      {new Date(
                        work.publicationDate ?? ''
                      ).toLocaleDateString()}
                    </Text>
                  </Ellipsis>
                </FlexRow>
                {work.doi && (
                  <Link
                    to={work.doi}
                    target="_blank"
                    onClick={e => e.stopPropagation()}
                  >
                    <FlexRow
                      alignItems="center"
                      justifyContent="start"
                      className="gap-2"
                    >
                      <Text variant="body-xs" className="text-blue-700">
                        View full article
                      </Text>
                      <ExternalLink className="w-4 h-4 text-blue-700" />
                    </FlexRow>
                  </Link>
                )}
              </FlexCol>
              <FlexRow gap="2" justifyContent="start" alignItems="center">
                {shouldDisplayAccessType && (
                  <Badge variant="outline" className="w-fit min-h-6">
                    <FlexRow
                      alignItems="center"
                      justifyContent="center"
                      className="gap-2"
                    >
                      {icon && <span>{icon}</span>}
                      <Text variant="body-xs" color="primary">
                        {textToDisplay}
                      </Text>
                    </FlexRow>
                  </Badge>
                )}

                {!work.fullContent && (
                  <FlexRow
                    alignItems="center"
                    justifyContent="start"
                    className="gap-2"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUploadClick}
                      loading={isUploading}
                    >
                      <Plus className="w-2 h-2" />
                      Add full paper
                    </Button>
                  </FlexRow>
                )}
              </FlexRow>
            </FlexCol>
          );
        },
      },
      {
        id: 'inclusionStatus',
        accessorKey: 'inclusionStatus',
        header: () => (
          <Text variant="body-sm" color="primary">
            Screening Decision
          </Text>
        ),
        cell: ({ row }) => {
          const { updatePaperScreeningResult } =
            useUpdatePaperScreeningResultService();

          const { updatePaperScreeningValidationStatus } =
            useUpdatePaperScreeningValidationStatusService();

          const { updatePaperScreeningResultJustification } =
            useUpdatePaperScreeningResultJustificationService();

          const [status, setStatus] = useState<
            ('include' | 'exclude' | 'inconclusive') | undefined
          >(row.original.screeningResult);
          const [justification, setJustification] = useState<
            string | undefined
          >(row.original.justification);
          const [validationStatus, setValidationStatus] = useState<
            'not_validated' | 'validated'
          >(row.original.validationStatus);

          const handleUpdatePaperScreeningResult =
            (status: 'include' | 'exclude') => () => {
              if (validationStatus === 'validated') {
                return;
              }
              updatePaperScreeningResult({
                paperScreeningId: row.original.id,
                data: { screeningResult: status },
              });
              setStatus(status);
            };

          const handleUpdatePaperScreeningValidationStatus =
            (validationStatus: 'not_validated' | 'validated') => () => {
              updatePaperScreeningValidationStatus({
                paperScreeningId: row.original.id,
                data: { validationStatus },
              });
              setValidationStatus(validationStatus);
            };

          const handleUpdatePaperScreeningResultJustification = (
            e: ChangeEvent<HTMLTextAreaElement>
          ) => {
            setJustification(e.target.value);
          };

          const saveJustification = () => {
            if (!justification) {
              setJustification(row.original.justification);
              toast.error('Justification is required');
              return;
            }
            updatePaperScreeningResultJustification(
              {
                paperScreeningId: row.original.id,
                data: { justification },
              },
              {
                onSuccess: () => {
                  toast.success('Justification saved');
                },
                onError: () => {
                  setJustification(row.original.justification);
                  toast.error('Failed to save justification');
                },
              }
            );
          };

          const isGenerating = row.original.isGenerating;

          const hasData =
            row.original.criteriaResults.some(
              criteriaResult =>
                criteriaResult.textValue !== undefined ||
                criteriaResult.generationStatus === 'completed' ||
                criteriaResult.generationStatus === 'error'
            ) ||
            !!row.original.screeningResult ||
            !!row.original.justification;

          useEffect(() => {
            setStatus(row.original.screeningResult);
          }, [row.original.screeningResult]);

          useEffect(() => {
            setJustification(row.original.justification);
          }, [row.original.justification]);

          useEffect(() => {
            setValidationStatus(row.original.validationStatus);
          }, [row.original.validationStatus]);

          const disabledValidation =
            status === 'inconclusive' ||
            status === undefined ||
            !justification?.trim();

          const disabledValidationReason =
            status === 'inconclusive'
              ? 'You cannot validate a paper screening if the status is inconclusive'
              : status === undefined
                ? 'You cannot validate a paper screening if the status is not set'
                : !justification?.trim()
                  ? 'You cannot validate a paper screening if the justification is not set'
                  : undefined;

          let background, textColor, iconColor, icon;

          if (status === 'include') {
            background = 'bg-green-50';
            textColor = 'text-green-800';
            iconColor = 'text-green-600';
            icon = <Check className="w-4 h-4" />;
          }
          if (status === 'exclude') {
            background = 'bg-red-100';
            textColor = 'text-red-800';
            iconColor = 'text-red-600';
            icon = <Ban className="w-4 h-4" />;
          }
          if (status === 'inconclusive') {
            background = 'bg-yellow-100';
            textColor = 'text-yellow-900';
            iconColor = 'text-yellow-700';
            icon = <CircleHelp className="w-4 h-4" />;
          }
          if (status === undefined) {
            background = 'bg-gray-100';
            textColor = 'text-gray-800';
            iconColor = 'text-gray-600';
          }

          if (isGenerating) {
            return (
              <FlexCol w="full" h="full" p="2">
                <Skeleton className="h-4" />
              </FlexCol>
            );
          }

          if (!hasData) {
            return null;
          }

          return (
            <FlexCol gap="4" justifyContent="between" className="px-2">
              <FlexRow className="w-full justify-between" alignItems="center">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    disabled={validationStatus === 'validated'}
                    className="w-fit"
                  >
                    <FlexRow
                      alignItems="center"
                      gap="2"
                      justifyContent="center"
                      className={cn(background, 'p-1.5 rounded-xl h-full ')}
                    >
                      {icon && <span className={iconColor}>{icon}</span>}
                      <Text
                        variant="body-sm"
                        className={`capitalize ${textColor}`}
                      >
                        {status ?? 'Set status'}
                      </Text>
                      {validationStatus === 'not_validated' && <ChevronDown />}
                    </FlexRow>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onClick={handleUpdatePaperScreeningResult('include')}
                    >
                      Include
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleUpdatePaperScreeningResult('exclude')}
                    >
                      Exclude
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {validationStatus === 'validated' ? (
                  <Button
                    variant="outline"
                    onClick={handleUpdatePaperScreeningValidationStatus(
                      'not_validated'
                    )}
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </Button>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Button
                          disabled={disabledValidation}
                          onClick={handleUpdatePaperScreeningValidationStatus(
                            'validated'
                          )}
                        >
                          <Check className="w-4 h-4" />
                          Validate
                        </Button>
                      </div>
                    </TooltipTrigger>
                    {disabledValidation && (
                      <TooltipContent>
                        {disabledValidationReason}
                      </TooltipContent>
                    )}
                  </Tooltip>
                )}
              </FlexRow>
              <FlexCol gap="1">
                <Text className="uppercase text-xs font-semibold text-neutral-600">
                  Justification:
                </Text>
                {validationStatus === 'validated' ? (
                  <Text variant="body-sm" color="primary">
                    {justification}
                  </Text>
                ) : (
                  <Textarea
                    value={justification}
                    onBlur={saveJustification}
                    onChange={handleUpdatePaperScreeningResultJustification}
                  />
                )}
              </FlexCol>
            </FlexCol>
          );
        },
        meta: {
          headerClassName:
            'w-[400px] min-w-[400px] max-w-[400px] border-r border-gray-200',
          cellClassName:
            'font-medium w-[400px] min-w-[400px] max-w-[400px] align-top border-r border-gray-200',
        },
      },
      ...criteriaColumns,
    ];
  }, [fullTextScreening, setSelectedPaperScreening, data]);

  return columns;
};
